import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { useCapability } from '../utils/capabilityContext';
import { useTranslation } from 'react-i18next';

const Sidebar = ({ isCollapsed, onToggleCollapse }) => {
  const isAdminAuthenticated = Boolean(localStorage.getItem('admin_token'));
  const { currentUser } = useAuth();
  const { debugEnabled } = useCapability();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');

  // SVG图标组件
  const iconComponents = {
    dashboard: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9,22 9,12 15,12 15,22"/>
      </svg>
    ),
    reports: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10,9 9,9 8,9"/>
      </svg>
    ),
    reviewedReports: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <polyline points="9,15 11,17 15,13"/>
      </svg>
    ),
    appointmentOrders: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
        <path d="M8 14h3"/>
        <path d="M8 18h8"/>
      </svg>
    ),
    debugDemo: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4"/>
        <path d="M12 18v4"/>
        <path d="M4.93 4.93l2.83 2.83"/>
        <path d="M16.24 16.24l2.83 2.83"/>
        <path d="M2 12h4"/>
        <path d="M18 12h4"/>
        <path d="M4.93 19.07l2.83-2.83"/>
        <path d="M16.24 7.76l2.83-2.83"/>
      </svg>
    ),
    doctors: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
        <path d="M12 2v4"/>
        <path d="M12 10v4"/>
        <path d="M8 6h8"/>
      </svg>
    ),
    collapse: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    ),
    expand: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    )
  };
  const menuItems = [
    {
      id: 'dashboard',
      label: t('dashboard.title'),
      icon: 'dashboard',
      path: '/'
    }
  ];

  if (currentUser) {
    menuItems.push(
      {
        id: 'reports',
        label: t('dashboard.pendingReports'),
        icon: 'reports',
        path: '/reports'
      },
      {
        id: 'reviewed-reports',
        label: t('dashboard.reviewedReports'),
        icon: 'reviewedReports',
        path: '/reviewed-reports'
      },
      {
        id: 'appointment-orders',
        label: isZh ? '复诊预约单' : 'Appointment orders',
        icon: 'appointmentOrders',
        path: '/appointment-orders'
      }
    );
  }

  if (isAdminAuthenticated) {
    menuItems.push({
      id: 'doctors',
      label: t('dashboard.doctorManagement'),
      icon: 'doctors',
      path: '/doctors'
    });

    if (debugEnabled) {
      menuItems.push({
        id: 'admin-debug-demo',
        label: t('debugDemo.menuLabel'),
        icon: 'debugDemo',
        path: '/admin/debug-demo'
      });
    }
  }

  // 检查当前路径是否匹配菜单项
  const isActiveMenuItem = (path) => {
    const currentPath = location.pathname;
    
    if (path === '/') {
      return currentPath === '/';
    }
    
    // 特殊处理报告相关路径
    if (path === '/reports') {
      // 待审核报告菜单项：包括 /reports 和 /reports/:id/review
      return currentPath === '/reports' || currentPath.match(/^\/reports\/\d+\/review$/);
    }
    
    if (path === '/reviewed-reports') {
      // 已审核报告菜单项：包括 /reviewed-reports 和 /reports/:id/view
      return currentPath.startsWith('/reviewed-reports') || currentPath.match(/^\/reports\/\d+\/view$/);
    }

    if (path === '/appointment-orders') {
      return currentPath.startsWith('/appointment-orders');
    }
    
    return currentPath.startsWith(path);
  };

  return (
    <aside className="app-sidebar">
      <div className="app-sidebar__brand">
        <div className="app-sidebar__brand-mark">
          AC
        </div>
        <div className="app-sidebar__brand-copy">
          <h2 className="app-sidebar__brand-title">
            {isCollapsed ? 'AI' : 'AI Clinic'}
          </h2>
          {!isCollapsed && (
            <span className="app-sidebar__brand-subtitle">
              Doctor Console
            </span>
          )}
        </div>
      </div>

      <nav className="app-sidebar__nav">
        {menuItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`app-sidebar__link ${isActiveMenuItem(item.path) ? 'is-active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            <span className="app-sidebar__icon">
              {iconComponents[item.icon]()}
            </span>
            {!isCollapsed && <span>{item.label}</span>}
          </button>
        ))}
      </nav>

      <div className="app-sidebar__footer">
        {!isCollapsed && (
          <span className="app-sidebar__brand-subtitle">
            {currentUser ? currentUser.name : isAdminAuthenticated ? t('dashboard.admin') : t('dashboard.visitor')}
          </span>
        )}
        <button
          type="button"
          className="app-sidebar__link"
          onClick={onToggleCollapse}
        >
          <span className="app-sidebar__icon">
            {iconComponents[isCollapsed ? 'expand' : 'collapse']()}
          </span>
          {!isCollapsed && <span>{t('dashboard.collapse')}</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
